import { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import "../assets/styles/checkout.css";
import "../assets/styles/pages.css";
import Button from "../components/Button";
import FormDocumentInput from "../components/FormDocumentInput";
import FormInput from "../components/FormInput";
import FormPhoneInput from "../components/FormPhoneInput";
import { UserContext } from "../context/UserContext";
import UserService from "../services/UserService";
import Utils from "../utils/Utils";

const Cadastro = () => {
  const { loginWithToken, refreshMe } = useContext(UserContext);

  const [email, setEmail] = useState("");
  const [birth, setBirth] = useState("");
  const [pass, setPass] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [cpf, setCpf] = useState("");
  const [documentCountry, setDocumentCountry] = useState("BR");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  // NOVO: usuário local detectado (anônimo / sem senha)
  const [localUser, setLocalUser] = useState(null);
  const [mode, setMode] = useState("register"); // 'register' | 'complete'
  const [feedback, setFeedback] = useState("");

  const isValidDate = (dateStr) => {
    const m = String(dateStr || "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return false;
    const [, d, mo, y] = m;
    const isoDate = `${y}-${mo}-${d}T12:00:00`;
    const dateObj = new Date(isoDate);
    if (isNaN(dateObj.getTime())) return false;
    if (
      dateObj.getDate() !== Number(d) ||
      dateObj.getMonth() + 1 !== Number(mo)
    )
      return false;

    const todayObj = new Date();
    todayObj.setHours(23, 59, 59, 999);
    if (dateObj > todayObj) return false;
    return true;
  };

  const isValidEmailFormat = (emailStr) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr);
  };

  const isBrazil = documentCountry === "BR";

  const isValidPassport = (value) => {
    return /^[A-Z0-9]{1,9}$/.test(String(value || ""));
  };

  const sanitizePassport = (value) => {
    return String(value || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 9);
  };

  const possibleKeys = useMemo(
    () => ["currentUser", "userData", "usrData"],
    [],
  );

  useEffect(() => {
    for (const k of possibleKeys) {
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      try {
        const obj = JSON.parse(raw);
        if (obj?.usr_email && obj?.usr_id) {
          setLocalUser(obj);
          setEmail((e) => e || obj.usr_email);
          setFirstName((n) => n || (obj.usr_name || "").split(" ")[0]);
          setLastName(
            (n) => n || (obj.usr_name || "").split(" ").slice(1).join(" "),
          );
          // Se backend não armazenou senha (não sabemos), assume modo completar
          setMode("complete");
          break;
        }
      } catch {
        /* ignore */
      }
    }
  }, [possibleKeys]);

  const isDisabled =
    loading ||
    !firstName.trim() ||
    !lastName.trim() ||
    !email.trim() ||
    !pass.trim() ||
    !birth.trim() ||
    !cpf.trim() ||
    !phone ||
    !isValidPhoneNumber(phone) ||
    (!isBrazil && !isValidPassport(cpf)) ||
    (isBrazil && !Utils.validateCPF(cpf)) ||
    pass.length < 6 ||
    !email.includes("@");

  // Máscara para data de nascimento (DD/MM/AAAA)
  const maskBirth = (value) => {
    if (!value) return "";
    // Remove tudo que não é dígito
    const digits = value.replace(/\D/g, "").slice(0, 8); // limita a 8 dígitos
    const parts = [];
    if (digits.length <= 2) {
      parts.push(digits);
    } else if (digits.length <= 4) {
      parts.push(digits.slice(0, 2));
      parts.push(digits.slice(2));
    } else {
      parts.push(digits.slice(0, 2));
      parts.push(digits.slice(2, 4));
      parts.push(digits.slice(4));
    }
    return parts.join("/");
  };

  // Removida a máscara manual de telefone, agora usamos react-phone-number-input

  // Máscara para CPF (XXX.XXX.XXX-XX)
  const maskCpf = (value) => {
    if (!value) return "";
    const digits = value.replace(/\D/g, "").slice(0, 11); // limita a 11 dígitos
    let out = "";
    if (digits.length <= 3) {
      out = digits;
    } else if (digits.length <= 6) {
      out = `${digits.slice(0, 3)}.${digits.slice(3)}`;
    } else if (digits.length <= 9) {
      out = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    } else {
      out = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(
        6,
        9,
      )}-${digits.slice(9)}`;
    }
    return out;
  };

  // Converte DD/MM/AAAA -> YYYY-MM-DD para o backend
  const toSqlBirth = (value) => {
    const m = String(value || "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return "";
    const [, d, mo, y] = m;
    return `${y}-${mo}-${d}`;
  };

  const tryCompleteAnonymous = async () => {
    const birthSql = toSqlBirth(birth);
    if (!birthSql) throw new Error("Data de nascimento inválida.");

    const birthDateObj = new Date(birthSql + "T12:00:00");
    const todayObj = new Date();
    todayObj.setHours(23, 59, 59, 999);
    if (birthDateObj > todayObj) {
      throw new Error("A data de nascimento não pode ser no futuro.");
    }
    // Payload para completar cadastro (definir senha + dados faltantes)
    const parsedPhone = phone ? parsePhoneNumber(phone) : null;
    const ddi = parsedPhone?.countryCallingCode
      ? "+" + parsedPhone.countryCallingCode
      : "+55";
    const nationalNumber = parsedPhone?.nationalNumber || phone;

    const data = await UserService.completarCadastro({
      usr_id: localUser.usr_id,
      usr_email: email.trim(),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      usr_doc_type: documentCountry,
      usr_cpf: cpf.trim(),
      phone_ddi: ddi,
      phone: nationalNumber,
      usr_birth: birthSql,
      usr_password: pass,
    });
    if (!data || data.status !== 1) {
      throw new Error(data?.message || "Falha ao completar cadastro.");
    }
    return data;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (isDisabled) return;
    setLoading(true);
    setFeedback("");

    try {
      if (mode === "complete" && localUser) {
        // Pula tentativa de criar novo e já completa
        await tryCompleteAnonymous();
        // Sincroniza o estado de autenticação do front (PATCH não retorna token novo).
        // Navegação fica a cargo do ModalLogin, que respeita o resumeType pendente
        // (ex.: retomar o fluxo do Básico) em vez de sempre ir para /cadastro-full.
        await refreshMe();
        return;
      }

      // Fluxo normal de registro
      const birthSql = toSqlBirth(birth);
      if (!birthSql) throw new Error("Data de nascimento inválida.");

      const birthDateObj = new Date(birthSql + "T12:00:00");
      const todayObj = new Date();
      todayObj.setHours(23, 59, 59, 999);
      if (birthDateObj > todayObj) {
        throw new Error("A data de nascimento não pode ser no futuro.");
      }

      const parsedPhone = phone ? parsePhoneNumber(phone) : null;
      const ddi = parsedPhone?.countryCallingCode
        ? "+" + parsedPhone.countryCallingCode
        : "+55";
      const nationalNumber = parsedPhone?.nationalNumber || phone;

      const res = await UserService.register({
        email: email.trim(),
        password: pass,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        doc_type: documentCountry,
        birth: birthSql,
        cpf: cpf.trim(),
        phone_ddi: ddi,
        phone: nationalNumber,
      });

      if (res && res.status === "success") {
        if (res.data?.token) {
          await loginWithToken(res.data.token, res.data.user);
        }
        // Navegação movida para o ModalLogin que engloba este componente
      } else {
        // Se já existe e temos localUser -> mudar para completar
        if (/já.*cadastr/i.test(res?.message || "") && localUser) {
          setMode("complete");
          setFeedback(
            "Detectamos um cadastro anterior. Defina sua senha para completar a conta.",
          );
        } else if (/já.*cadastr/i.test(res?.message || "")) {
          // Sem localUser conhecido: sugere login
          setFeedback("Este e-mail já possui cadastro. Faça login.");
        } else {
          setFeedback(res?.message || "Erro ao criar conta.");
        }
      }
    } catch (error) {
      console.error("[Cadastro] erro:", error);
      const apiErrors = error.response?.data?.errors;
      if (apiErrors && Object.keys(apiErrors).length > 0) {
        const msgs = Object.values(apiErrors).flat().join("\n");
        setFeedback(msgs);
      } else {
        setFeedback(
          error.response?.data?.message || error.message || "Erro inesperado.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Remover a verificação de autenticação aqui pois o ModalLogin ou a página Pai já lida com isso.

  return (
    <div className="Checkout">
      <Helmet>
        <title>{mode === "complete" ? "Completar cadastro" : "Cadastro"}</title>
        <meta name="description" content="Crie ou complete sua conta." />
      </Helmet>

      <section className="Cadastro">
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-12">
              <h2>
                <strong>
                  {mode === "complete"
                    ? "COMPLETE SUA CONTA"
                    : "CRIE SUA CONTA"}
                </strong>
              </h2>

              {feedback && (
                <div
                  className={`alert p-2 mb-3 small ${
                    feedback.toLowerCase().includes("erro")
                      ? "alert-danger"
                      : "alert-warning"
                  }`}
                  style={{ whiteSpace: "pre-line" }}
                >
                  {feedback}
                </div>
              )}

              <form onSubmit={handleRegister}>
                <FormInput
                  required
                  float
                  type="text"
                  name="firstName"
                  label="Nome"
                  placeholder="Primeiro nome"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <FormInput
                  required
                  float
                  type="text"
                  name="lastName"
                  label="Sobrenome"
                  placeholder="Sobrenome"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
                <FormInput
                  required
                  float
                  type="text"
                  name="birth"
                  label="Data de nascimento"
                  placeholder="DD/MM/AAAA"
                  value={birth}
                  onChange={(e) => setBirth(maskBirth(e.target.value))}
                  onBlur={() => {
                    if (birth && !isValidDate(birth)) setBirth("");
                  }}
                />
                <FormPhoneInput
                  required
                  float
                  name="phone"
                  label="Telefone"
                  placeholder="Informe seu telefone"
                  value={phone}
                  onChange={setPhone}
                  invalid={!!phone && !isValidPhoneNumber(phone)}
                />
                <FormDocumentInput
                  required
                  float
                  name="cpf"
                  label={isBrazil ? "CPF" : "Passaporte"}
                  placeholder={
                    isBrazil ? "Informe seu CPF" : "Informe seu passaporte"
                  }
                  value={cpf}
                  country={documentCountry}
                  invalid={
                    !!cpf &&
                    (isBrazil ? !Utils.validateCPF(cpf) : !isValidPassport(cpf))
                  }
                  onCountryChange={(nextCountry) => {
                    setDocumentCountry(nextCountry);
                    setCpf((current) =>
                      nextCountry === "BR"
                        ? maskCpf(current)
                        : sanitizePassport(current),
                    );
                  }}
                  onChange={(nextValue) =>
                    setCpf(
                      isBrazil
                        ? maskCpf(nextValue)
                        : sanitizePassport(nextValue),
                    )
                  }
                />
                <FormInput
                  required
                  float
                  type="email"
                  name="email"
                  label="E-mail"
                  placeholder="Informe seu melhor e-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!!localUser} // email fixo se veio de fluxo anônimo
                  onBlur={() => {
                    if (email && !isValidEmailFormat(email)) setEmail("");
                  }}
                />
                <FormInput
                  required
                  float
                  type="password"
                  name="password"
                  label={mode === "complete" ? "Defina uma senha" : "Senha"}
                  placeholder="Informe sua senha"
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                />
                <Button
                  type="submit"
                  classes="mt-3"
                  disabled={isDisabled}
                  label={
                    loading
                      ? "Processando..."
                      : mode === "complete"
                        ? "Salvar senha e continuar"
                        : "Criar conta"
                  }
                />
                {mode === "complete" && !loading && (
                  <div className="mt-3">
                    <small className="text-muted">
                      Estamos vinculando sua conta criada anteriormente sem
                      senha.
                    </small>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cadastro;
